import api from './api';

// Types
export interface Parceiro {
  id: number;
  nome: string;
  categoria: string;
  desconto: string;
  logo?: string;
  descricao?: string;
  endereco?: string;
  telefone?: string;
  website?: string;
  destaque: boolean;
  status: string;
  created_at: string;
}

export interface NivelBeneficio {
  id: number;
  nome: string;
  pontos_minimos: number;
  pontos_maximos: number;
  beneficios: string;
  cor: string;
  icone: string;
  status: string;
}

export interface MiniApp {
  id: number;
  nome: string;
  descricao: string;
  icone: string;
  categoria: string;
  url: string;
  preco: number;
  preco_original?: number;
  gratuito: boolean;
  destaque: boolean;
  status: string;
  ordem: number;
  total_compras?: number;
  created_at: string;
}

export interface Novidade {
  id: number;
  titulo: string;
  descricao: string;
  conteudo?: string;
  tipo: string;
  imagem?: string;
  destaque: boolean;
  status: string;
  data_publicacao: string;
  created_at: string;
}

export interface FeatureRequest {
  id: number;
  titulo: string;
  descricao: string;
  categoria: string;
  status: string;
  total_votos: number;
  created_at: string;
}

export interface Recurso {
  id: number;
  titulo: string;
  descricao: string;
  tipo: string;
  url: string;
  icone: string;
  categoria: string;
  ordem: number;
  status: string;
}

export interface HubStats {
  total_parceiros: number;
  total_apps: number;
  total_apps_vendidos: number;
  receita_apps: number;
  total_pontos_distribuidos: number;
  total_referrals: number;
  total_novidades: number;
  needsSetup?: boolean;
}

class HubService {
  // ============================================
  // STATS / DASHBOARD
  // ============================================

  async getStats(): Promise<HubStats> {
    const response = await api.get<{
      success: boolean;
      data?: HubStats;
      error?: string;
      needsSetup?: boolean;
    }>('/admin/hub/stats');

    if (response.data.needsSetup) {
      return {
        total_parceiros: 0,
        total_apps: 0,
        total_apps_vendidos: 0,
        receita_apps: 0,
        total_pontos_distribuidos: 0,
        total_referrals: 0,
        total_novidades: 0,
        needsSetup: true,
      };
    }

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar estatísticas');
  }

  // ============================================
  // PARCEIROS
  // ============================================

  async getParceiros(): Promise<Parceiro[]> {
    const response = await api.get<{
      success: boolean;
      data?: Parceiro[];
      error?: string;
    }>('/admin/hub/parceiros');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    return [];
  }

  async createParceiro(data: Partial<Parceiro>): Promise<Parceiro> {
    const response = await api.post<{
      success: boolean;
      data?: Parceiro;
      error?: string;
    }>('/admin/hub/parceiros', data);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao criar parceiro');
  }

  async updateParceiro(id: number, data: Partial<Parceiro>): Promise<Parceiro> {
    const response = await api.put<{
      success: boolean;
      data?: Parceiro;
      error?: string;
    }>(`/admin/hub/parceiros/${id}`, data);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao atualizar parceiro');
  }

  async deleteParceiro(id: number): Promise<void> {
    const response = await api.delete<{
      success: boolean;
      error?: string;
    }>(`/admin/hub/parceiros/${id}`);

    if (!response.data.success) {
      throw new Error(response.data.error || 'Falha ao eliminar parceiro');
    }
  }

  // ============================================
  // MINI-APPS
  // ============================================

  async getApps(): Promise<MiniApp[]> {
    const response = await api.get<{
      success: boolean;
      data?: MiniApp[];
      error?: string;
    }>('/admin/hub/apps');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    return [];
  }

  async createApp(data: Partial<MiniApp>): Promise<MiniApp> {
    const response = await api.post<{
      success: boolean;
      data?: MiniApp;
      error?: string;
    }>('/admin/hub/apps', data);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao criar app');
  }

  async updateApp(id: number, data: Partial<MiniApp>): Promise<MiniApp> {
    const response = await api.put<{
      success: boolean;
      data?: MiniApp;
      error?: string;
    }>(`/admin/hub/apps/${id}`, data);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao atualizar app');
  }

  async deleteApp(id: number): Promise<void> {
    const response = await api.delete<{
      success: boolean;
      error?: string;
    }>(`/admin/hub/apps/${id}`);

    if (!response.data.success) {
      throw new Error(response.data.error || 'Falha ao eliminar app');
    }
  }

  // ============================================
  // NIVEIS
  // ============================================

  async getNiveis(): Promise<NivelBeneficio[]> {
    const response = await api.get<{
      success: boolean;
      data?: NivelBeneficio[];
      error?: string;
    }>('/admin/hub/niveis');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    return [];
  }

  async createNivel(data: Partial<NivelBeneficio>): Promise<NivelBeneficio> {
    const response = await api.post<{
      success: boolean;
      data?: NivelBeneficio;
      error?: string;
    }>('/admin/hub/niveis', data);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao criar nível');
  }

  async updateNivel(id: number, data: Partial<NivelBeneficio>): Promise<NivelBeneficio> {
    const response = await api.put<{
      success: boolean;
      data?: NivelBeneficio;
      error?: string;
    }>(`/admin/hub/niveis/${id}`, data);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao atualizar nível');
  }

  async deleteNivel(id: number): Promise<void> {
    const response = await api.delete<{
      success: boolean;
      error?: string;
    }>(`/admin/hub/niveis/${id}`);

    if (!response.data.success) {
      throw new Error(response.data.error || 'Falha ao eliminar nível');
    }
  }

  // ============================================
  // NOVIDADES
  // ============================================

  async getNovidades(): Promise<Novidade[]> {
    const response = await api.get<{
      success: boolean;
      data?: Novidade[];
      error?: string;
    }>('/admin/hub/novidades');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    return [];
  }

  async createNovidade(data: Partial<Novidade>): Promise<Novidade> {
    const response = await api.post<{
      success: boolean;
      data?: Novidade;
      error?: string;
    }>('/admin/hub/novidades', data);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao criar novidade');
  }

  async updateNovidade(id: number, data: Partial<Novidade>): Promise<Novidade> {
    const response = await api.put<{
      success: boolean;
      data?: Novidade;
      error?: string;
    }>(`/admin/hub/novidades/${id}`, data);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao atualizar novidade');
  }

  async deleteNovidade(id: number): Promise<void> {
    const response = await api.delete<{
      success: boolean;
      error?: string;
    }>(`/admin/hub/novidades/${id}`);

    if (!response.data.success) {
      throw new Error(response.data.error || 'Falha ao eliminar novidade');
    }
  }

  // ============================================
  // RECURSOS (FAQ, guias, contactos, quick actions)
  // ============================================

  async getRecursos(): Promise<any[]> {
    const response = await api.get<{ success: boolean; data?: any[]; error?: string }>('/admin/hub/recursos');
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.error || 'Falha ao carregar recursos');
  }

  async createRecurso(data: any): Promise<any> {
    const response = await api.post<{ success: boolean; data?: any; error?: string }>('/admin/hub/recursos', data);
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.error || 'Falha ao criar recurso');
  }

  async updateRecurso(id: number, data: any): Promise<any> {
    const response = await api.put<{ success: boolean; data?: any; error?: string }>(`/admin/hub/recursos/${id}`, data);
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.error || 'Falha ao atualizar recurso');
  }

  async deleteRecurso(id: number): Promise<void> {
    const response = await api.delete<{ success: boolean; error?: string }>(`/admin/hub/recursos/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Falha ao eliminar recurso');
  }

  // ============================================
  // FEATURE REQUESTS (propostas para votação)
  // ============================================

  async getFeatureRequests(): Promise<any[]> {
    const response = await api.get<{ success: boolean; data?: any[]; error?: string }>('/admin/hub/feature-requests');
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.error || 'Falha ao carregar feature requests');
  }

  async createFeatureRequest(data: any): Promise<any> {
    const response = await api.post<{ success: boolean; data?: any; error?: string }>('/admin/hub/feature-requests', data);
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.error || 'Falha ao criar feature request');
  }

  async updateFeatureRequest(id: number, data: any): Promise<any> {
    const response = await api.put<{ success: boolean; data?: any; error?: string }>(`/admin/hub/feature-requests/${id}`, data);
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error(response.data.error || 'Falha ao atualizar feature request');
  }

  async deleteFeatureRequest(id: number): Promise<void> {
    const response = await api.delete<{ success: boolean; error?: string }>(`/admin/hub/feature-requests/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Falha ao eliminar feature request');
  }

  // ============================================
  // SETUP
  // ============================================

  async setupTables(): Promise<{ message: string; created?: string[] }> {
    const response = await api.post<{
      success: boolean;
      data?: { message: string; created?: string[] };
      error?: string;
    }>('/admin/hub/setup');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao configurar tabelas');
  }
}

export default new HubService();
